"use client";

import { motion } from "motion/react";

/* ── Boundary diagrams ─────────────────────────────────────────────────
   One drawing, five states. Every idea on this page is the same geometry:
   my circle, the other person's circle, and what happens where they meet.
   Reusing one figure is the point — the reader learns to read it once in
   "Ideea din care pornim" and then recognises the three patterns instantly.

   Structure strokes inherit `currentColor`, so the same component works on
   cream and inside the dark colour passage. The one element that carries the
   meaning takes `accent`, and it is the only thing that animates: it draws
   itself in when the figure comes into view, which is what the eye should
   follow. No transforms anywhere — a transformed ancestor would break the
   fixed background layer in <ColorStage>. */

export type BoundaryVariant =
  | "neutru"
  | "control"
  | "limita"
  | "permeabile"
  | "rigide"
  | "flexibile";

const W = 240;
const H = 128;
const CY = 60;

/** Draw-on for the meaningful stroke. Opacity + pathLength only. */
const draw = {
  hidden: { pathLength: 0, opacity: 0 },
  show: {
    pathLength: 1,
    opacity: 1,
    transition: { duration: 0.9, ease: [0.2, 0.65, 0.35, 1] as const },
  },
};

function Label({ x, children }: { x: number; children: string }) {
  return (
    <text
      x={x}
      y={H - 10}
      textAnchor="middle"
      fontSize="11"
      fillOpacity="0.5"
      fill="currentColor"
      className="font-sans"
    >
      {children}
    </text>
  );
}

export default function BoundaryDiagram({
  variant,
  accent,
  className = "",
}: {
  variant: BoundaryVariant;
  accent: string;
  className?: string;
}) {
  return (
    <motion.svg
      viewBox={`0 0 ${W} ${H}`}
      fill="none"
      aria-hidden
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.6 }}
      className={`h-auto w-full max-w-[240px] ${className}`}
    >
      {variant === "neutru" && (
        <>
          <circle cx="74" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" />
          <circle cx="166" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.4" strokeWidth="1.5" />
          <motion.path
            d={`M 120 ${CY - 44} L 120 ${CY + 44}`}
            stroke={accent}
            strokeWidth="2.5"
            strokeLinecap="round"
            variants={draw}
          />
          <Label x={74}>eu</Label>
          <Label x={166}>celălalt</Label>
        </>
      )}

      {variant === "control" && (
        <>
          {/* Two selves, side by side. The arrow starts in mine and lands
              inside theirs: an instruction about their behaviour. */}
          <circle cx="74" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.28" strokeWidth="1.5" />
          <circle cx="166" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.28" strokeWidth="1.5" />
          <motion.path
            d={`M 74 ${CY} L 176 ${CY}`}
            stroke={accent}
            strokeWidth="2"
            strokeLinecap="round"
            variants={draw}
          />
          <motion.path
            d={`M 168 ${CY - 6} L 176 ${CY} L 168 ${CY + 6}`}
            stroke={accent}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            variants={draw}
          />
          <Label x={74}>eu</Label>
          <Label x={166}>celălalt</Label>
        </>
      )}

      {variant === "limita" && (
        <>
          {/* Same two selves. The line sits on my own edge, and what I decide
              stops there. Nothing crosses into their circle. */}
          <circle cx="74" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.28" strokeWidth="1.5" />
          <circle cx="166" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.28" strokeWidth="1.5" />
          <motion.path
            d={`M 110 ${CY - 40} L 110 ${CY + 40}`}
            stroke={accent}
            strokeWidth="2.5"
            strokeLinecap="round"
            variants={draw}
          />
          <motion.path
            d={`M 74 ${CY} L 102 ${CY}`}
            stroke={accent}
            strokeWidth="2"
            strokeLinecap="round"
            variants={draw}
          />
          <Label x={74}>eu</Label>
          <Label x={166}>celălalt</Label>
        </>
      )}

      {variant === "permeabile" && (
        <>
          {/* My edge is dotted and theirs sits deep inside it: whatever they
              bring simply arrives. */}
          <circle
            cx="96"
            cy={CY}
            r="38"
            stroke="currentColor"
            strokeOpacity="0.3"
            strokeWidth="1.5"
            strokeDasharray="2 6"
            strokeLinecap="round"
          />
          <motion.circle
            cx="140"
            cy={CY}
            r="38"
            stroke={accent}
            strokeWidth="2"
            variants={draw}
          />
          <Label x={78}>eu</Label>
          <Label x={166}>celălalt</Label>
        </>
      )}

      {variant === "rigide" && (
        <>
          {/* A double wall and a wide gap. Safe, and out of reach. */}
          <circle cx="62" cy={CY} r="34" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" />
          <motion.circle
            cx="62"
            cy={CY}
            r="41"
            stroke={accent}
            strokeWidth="2.5"
            variants={draw}
          />
          <circle cx="184" cy={CY} r="34" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" />
          <Label x={62}>eu</Label>
          <Label x={184}>celălalt</Label>
        </>
      )}

      {variant === "flexibile" && (
        <>
          {/* The edge is there and it is mine, but it has an opening: contact
              stays possible without the circle dissolving. */}
          <circle cx="82" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" />
          <circle cx="158" cy={CY} r="36" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1.5" />
          <motion.path
            d={`M 120 ${CY - 40} L 120 ${CY - 14}`}
            stroke={accent}
            strokeWidth="2.5"
            strokeLinecap="round"
            variants={draw}
          />
          <motion.path
            d={`M 120 ${CY + 14} L 120 ${CY + 40}`}
            stroke={accent}
            strokeWidth="2.5"
            strokeLinecap="round"
            variants={draw}
          />
          <Label x={82}>eu</Label>
          <Label x={158}>celălalt</Label>
        </>
      )}
    </motion.svg>
  );
}
